import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { 
  Container, 
  Card, 
  CardContent, 
  Box, 
  Typography, 
  Button, 
  IconButton, 
  Grid, 
  Select, 
  MenuItem 
} from "@mui/material";
import { DataService } from "src/services/data-service";
import GenericInput from "src/components/input-components/GenericTextInput";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ImagePicker from "src/components/input-components/GenericImagePicker"; // Import ImagePicker

// Define TypeScript interfaces for Menu and Social items
interface MenuItem {
  Text: string;
  Link: string;
}

interface SocialItem {
  Platform: string;
  Link: string;
  IconClass: string;
}

interface HeaderConfig {
  LogoUrl: string;
  LogoLink: string;
  Navigation: MenuItem[];
  Social: SocialItem[];
}

// Default header config in case no data is returned
const defaultHeader: HeaderConfig = {
  LogoUrl: "",
  LogoLink: "/",
  Navigation: [],
  Social: []
};

// Social media platform options (with corresponding icon classes)
const socialPlatforms = [
  { platform: "Facebook", iconClass: "fab fa-facebook-f" },
  { platform: "X", iconClass: "fab fa-x-twitter" },
  { platform: "Twitter", iconClass: "fab fa-twitter" },
  { platform: "LinkedIn", iconClass: "fab fa-linkedin-in" },
  { platform: "Instagram", iconClass: "fab fa-instagram" },
  { platform: "YouTube", iconClass: "fab fa-youtube" },
  { platform: "GitHub", iconClass: "fab fa-github" },
];

const EditHeader: React.FC = () => {
  const [editedHeader, setEditedHeader] = useState<HeaderConfig>(defaultHeader);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [imagePickerOpen, setImagePickerOpen] = useState<boolean>(false);

  // Fetch header configuration from API
  useEffect(() => {
    const fetchHeader = async () => {
      setLoading(true);
      const res = await DataService.get("api/layout/header");

      if (!res.ok) {
        toast.error("Failed to load header data");
        setLoading(false);
        return;
      }

      const jsonData = await res.json();

      // The API returns an object with a "Data" property
      if (jsonData?.Data) {
        setEditedHeader(jsonData.Data);
      } else {
        toast.error("Invalid data format from API");
      }
      setLoading(false);
    };

    fetchHeader();
  }, []);

  // Handle changes for Logo, Navigation, and Social fields
  const handleChange = (
    field: keyof HeaderConfig,
    index: number | null,
    key: string | null,
    eventOrValue: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    setEditedHeader((prevState) => {
      const updated = { ...prevState };
      let value: string;

      if (typeof eventOrValue === "string") {
        value = eventOrValue;
      } else if (eventOrValue?.target) {
        value = eventOrValue.target.value;
      } else {
        return prevState;
      }

      if (field === "LogoUrl" || field === "LogoLink") {
        (updated as any)[field] = value;
      } else if (field === "Navigation" && index !== null && key) {
        updated.Navigation = [...prevState.Navigation];
        updated.Navigation[index][key] = value;
      } else if (field === "Social" && index !== null && key) {
        updated.Social = [...prevState.Social];
        updated.Social[index][key] = value;

        // When a social platform is selected, set its corresponding icon class automatically.
        if (key === "Platform") {
          const selectedPlatform = socialPlatforms.find((p) => p.platform === value);
          if (selectedPlatform) {
            updated.Social[index].IconClass = selectedPlatform.iconClass;
          }
        }
      }
      return updated;
    });
  };

  // Open the image picker modal
  const handleOpenImagePicker = () => {
    setImagePickerOpen(true);
  };

  // Handle image selection from the image picker
  const handleImageSelect = (imageUrl: string) => {
    setEditedHeader((prevState) => ({
      ...prevState,
      LogoUrl: imageUrl
    }));
    setImagePickerOpen(false);
  };

  // Navigation Items: add a new menu item
  const handleAddMenuItem = () => {
    setEditedHeader((prevState) => ({
      ...prevState,
      Navigation: [...prevState.Navigation, { Text: "", Link: "" }]
    }));
  };

  // Navigation Items: remove a menu item
  const handleRemoveMenuItem = (index: number) => {
    setEditedHeader((prevState) => ({
      ...prevState,
      Navigation: prevState.Navigation.filter((_, i) => i !== index)
    }));
  };

  // Social Links: add a new social item
  const handleAddSocial = () => {
    setEditedHeader((prevState) => ({
      ...prevState,
      Social: [...prevState.Social, { Platform: "", Link: "", IconClass: "" }]
    }));
  };

  // Social Links: remove a social item
  const handleRemoveSocial = (index: number) => {
    setEditedHeader((prevState) => ({
      ...prevState,
      Social: prevState.Social.filter((_, i) => i !== index)
    }));
  };

  // Save the header configuration to the API
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        Order: 1,
        Type: "HeaderV1",
        Data: editedHeader
      };

      const res = await DataService.put("api/layout/header", { config: JSON.stringify(payload) });

      if (!res.ok) {
        toast.error("Failed to update header");
        return;
      }

      toast.success("Header updated successfully!");
    } catch (error) {
      toast.error("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Helmet>
        <title>Edit Header</title>
      </Helmet>
      <Card elevation={3} sx={{ p: 3 }}>
        <CardContent>
          <Box 
            component="form" 
            onSubmit={handleSave} 
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            <Typography variant="h5" gutterBottom align="center">
              Header Configuration
            </Typography>

            {/* Logo Section */}
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h6">Logo</Typography>
              {editedHeader.LogoUrl && (
                <Box sx={{ mb: 1 }}>
                  <img 
                    src={editedHeader.LogoUrl} 
                    alt="Logo" 
                    style={{ maxWidth: "150px", maxHeight: "150px", objectFit: "contain" }} 
                  />
                </Box>
              )}
              <Button variant="outlined" onClick={handleOpenImagePicker}>
                Select Logo
              </Button>
            </Box>

            {/* Navigation Menu Section */}
            <Typography variant="h6" gutterBottom>
              Navigation Menu
            </Typography>
            {editedHeader.Navigation.map((item, index) => (
              <Grid container spacing={2} key={index} alignItems="center">
                <Grid item xs={5}>
                  <GenericInput
                    type="text"
                    name={`NavText-${index}`}
                    title="Menu Name"
                    value={item.Text}
                    onChange={(e) => handleChange("Navigation", index, "Text", e)}
                  />
                </Grid>
                <Grid item xs={5}>
                  <GenericInput
                    type="text"
                    name={`NavLink-${index}`}
                    title="Menu Link"
                    value={item.Link}
                    onChange={(e) => handleChange("Navigation", index, "Link", e)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton color="error" onClick={() => handleRemoveMenuItem(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddMenuItem}>
              Add Menu Item
            </Button>

            {/* Social Media Section */}
            <Typography variant="h6" gutterBottom>
              Social Links
            </Typography>
            {editedHeader.Social.map((item, index) => (
              <Grid container spacing={2} key={index} alignItems="center">
                <Grid item xs={4}>
                  <Select
                    value={item.Platform}
                    onChange={(e) => handleChange("Social", index, "Platform", e.target.value)}
                    fullWidth
                  >
                    {socialPlatforms.map((option) => (
                      <MenuItem key={option.platform} value={option.platform}>
                        {option.platform}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={6}>
                  <GenericInput
                    type="text"
                    name={`SocialLink-${index}`}
                    title="Social Link"
                    value={item.Link}
                    onChange={(e) => handleChange("Social", index, "Link", e)}
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton color="error" onClick={() => handleRemoveSocial(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddSocial}>
              Add Social Link
            </Button>

            {/* Save Button */}
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              disabled={submitting} 
              sx={{ mt: 2 }}
            >
              {submitting ? "Saving..." : "Save Header"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ImagePicker Modal */}
      {imagePickerOpen && (
        <ImagePicker
          open={imagePickerOpen}
          multiSelect={false}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleImageSelect}
        />
      )}
    </Container>
  );
};

export default EditHeader;
